import { QuestionElement } from "@/components/questionnaire/QuestionElements.tsx";
import { useQuestionnaireContext } from "@/components/questionnaire/questionnaire-context.ts";
import { Button, Skeleton, Title, Stack, HtmlViewer } from "@skailer-com/uikit";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export const QuestionnaireForm = () => {
  const {
    questionnaireForm,
    currentPage,
    totalPages,
    isLastPage,
    isSubmitting,
    isSaving,
    nextPage,
    previousPage,
    submitQuestionnaire,
  } = useQuestionnaireContext();
  const { t } = useTranslation();
  const formContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (formContainerRef.current) {
      formContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [currentPage]);

  if (!questionnaireForm) return <Skeleton className={"w-full h-10"} />;

  const currentFormPageData = questionnaireForm.pages[currentPage - 1];

  if (!currentFormPageData) {
    // todo surveys raise exception
    return;
  }

  return (
    <Stack gap={"sm"} ref={formContainerRef}>
      {(currentFormPageData.title || currentFormPageData.description) && totalPages > 1 && (
        <Stack gap={"lg"}>
          <Title header={currentFormPageData.title || ""} size={"lg"} />
          <HtmlViewer
            className={"pl-[31px] border-l border-dark mb-6"}
            content={currentFormPageData.description || ""}
          />
        </Stack>
      )}

      {currentFormPageData.sections.map((section, index) => (
        <Stack key={section.id} gap={"xl"}>
          {(section.title || section.description) && currentFormPageData.sections.length > 1 && (
            <Stack gap={"lg"}>
              <Title header={section.title || ""} size={"lg"} paddingTop={index > 0} />
              <HtmlViewer
                className={"pl-[31px] border-l border-dark"}
                content={section.description || ""}
              />
            </Stack>
          )}
          {section.elements.length > 0 && (
            <Stack gap={"sm"}>
              {section.elements.map((element, index) => (
                <div key={`element-${element.id}-${element.reference_id}`} className={"card-panel"}>
                  <QuestionElement
                    element={element}
                    isFirst={index === 0}
                    isLast={index === section.elements.length - 1}
                  />
                </div>
              ))}
            </Stack>
          )}
        </Stack>
      ))}

      <Stack direction="horizontal" className={"py-4 justify-between"}>
        <div>
          {currentPage > 1 && (
            <Button onClick={previousPage} size={"lg"} variant="secondary" type="button">
              {t("common:previous")}
            </Button>
          )}
        </div>

        <div>
          {!isLastPage ? (
            <Button onClick={nextPage} size={"lg"} type="button" isLoading={isSaving}>
              {t("common:continue")}
            </Button>
          ) : (
            <Button
              onClick={() => {
                submitQuestionnaire();
              }}
              size={"lg"}
              type="button"
              isLoading={isSubmitting}
            >
              {t("common:submit")}
            </Button>
          )}
        </div>
      </Stack>

      {totalPages > 1 && (
        <div className="text-center text-sm text-secondary mt-2">
          {t("common:of", { current: currentPage, total: totalPages })}
        </div>
      )}
    </Stack>
  );
};
